<script setup lang="ts">
import { computed } from 'vue';
import type { BidderInvoiceTotals } from '@neon-one/auctions-types';
import { SpendingMode } from './types';
import { formatCents } from '../../lib/formatCents';

const props = withDefaults(defineProps<{
  cents: number;
  mode?: SpendingMode;
  winningCount?: number;
  invoicePaid?: boolean;
  invoiceTotals?: BidderInvoiceTotals | null;
}>(), { mode: SpendingMode.PreClose, winningCount: 0, invoicePaid: false, invoiceTotals: null });

const money = computed(() => props.mode === SpendingMode.PostClose ? props.invoiceTotals : null);
const paid = computed(() => money.value ? money.value.dueCents === 0 : props.invoicePaid);
const display = computed(() => formatCents(money.value ? money.value.dueCents + money.value.paidCents : props.cents));
const subline = computed(() => {
  if (props.mode === SpendingMode.PreClose) {
    if (props.winningCount === 0) return "You've been outbid on everything";
    return `Total across ${props.winningCount} winning item${props.winningCount === 1 ? '' : 's'}`;
  }
  if (money.value && money.value.dueCents > 0 && money.value.paidCents > 0) {
    return `${formatCents(money.value.dueCents)} due · ${formatCents(money.value.paidCents)} paid`;
  }
  if (paid.value) return `Paid for ${props.winningCount} item${props.winningCount === 1 ? '' : 's'}`;
  return `Due at checkout for ${props.winningCount} item${props.winningCount === 1 ? '' : 's'}`;
});
</script>

<template>
  <div
    class="tracker"
    data-testid="spending-tracker"
  >
    <span
      class="tracker__value"
      data-testid="spending-tracker__value"
    >{{ display }}</span>
    <span
      v-if="subline"
      :class="['tracker__subline', paid && mode === SpendingMode.PostClose && 'tracker__subline--paid']"
    >{{ subline }}</span>
  </div>
</template>

<style scoped>
/* B5.1 §1 — exact committed/retained amount and caption typography. */
.tracker {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}
.tracker__value {
  font-size: var(--app-font-size-display);
  line-height: var(--app-line-height-display);
  font-weight: var(--app-font-weight-semibold);
}
.tracker__subline {
  font-size: var(--app-font-size-body-sm);
  line-height: var(--app-line-height-body-sm);
  color: var(--app-text-muted);
}
.tracker__subline--paid {
  color: var(--app-success);
  font-weight: var(--app-font-weight-medium);
}
</style>
